import { Controller } from "@hotwired/stimulus"
import { HlsPlayback } from "./hls_playback"

// The viewer over the Note's mosaic: the tile crops to compose and says only
// which kind it is, the dialog shows the thing whole. Native <dialog> carries
// the modality (Esc, focus trap, top layer); this controller only swaps the
// source in and out — it lives only while open, so the large variant, the
// video and the audio never load early.
//
// A tap anywhere closes, Journal-style, so the player has to keep its own
// taps: without that, reaching for play would close the dialog instead.
//
// How a video actually plays — original first, signed stream if there is one,
// original back if it fails — is HlsPlayback's business, shared with the
// Document's video sheet.
export default class extends Controller {
  static targets = ["dialog", "image", "video", "audio", "player", "caption"]

  open(event) {
    const element = this.elementFor(event.params.kind)
    if (!element) return

    if (element === this.audioTarget) {
      // Audio is the one kind with nothing to look at: the name carries it.
      this.playerTarget.src = event.params.url
      this.captionTarget.textContent = event.params.name
    } else if (element === this.videoTarget) {
      this.playback.play(event.params)
    } else {
      element.src = event.params.url
      if (element === this.imageTarget) element.alt = event.params.name
    }
    element.hidden = false
    this.dialogTarget.showModal()
  }

  close() {
    this.clear()
    this.dialogTarget.close()
  }

  keep(event) {
    event.stopPropagation()
  }

  // Wired to the player's own error event, which is how the native HLS path
  // reports a manifest or segment it cannot fetch.
  recoverOriginal() {
    this.playback?.fallBackToOriginal()
  }

  // Also wired to the dialog's native close event, so Esc empties it too
  clear() {
    if (this.hasVideoTarget) this.playback.stop()
    if (this.hasPlayerTarget) {
      this.playerTarget.pause()
      this.playerTarget.removeAttribute("src")
      this.playerTarget.load()
    }
    this.media.forEach((element) => {
      element.hidden = true
    })
    this.imageTarget.removeAttribute("src")
    this.imageTarget.alt = ""
    if (this.hasCaptionTarget) this.captionTarget.textContent = ""
  }

  get playback() {
    if (!this.hasVideoTarget) return null

    this.hlsPlayback ||= new HlsPlayback(this.videoTarget)

    return this.hlsPlayback
  }

  elementFor(kind) {
    if (kind === "photo") return this.imageTarget
    if (kind === "video") return this.hasVideoTarget ? this.videoTarget : null
    if (kind === "audio") return this.hasAudioTarget ? this.audioTarget : null

    return null
  }

  get media() {
    return [
      this.imageTarget,
      this.hasVideoTarget ? this.videoTarget : null,
      this.hasAudioTarget ? this.audioTarget : null
    ].filter(Boolean)
  }
}
